import { MainLayout } from '@/components/layout/MainLayout';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDesktopModeSetting } from '@/lib/hooks/useDesktopMode';
import { getSettings, updateSettings, type Settings } from '@/lib/api/settings';
import { CLAUSE_TEXT } from '@/lib/api/agreements';
import { IntegrationsTab } from './IntegrationsTab';
import { InvitesTab } from './InvitesTab';
import { useToast } from '@/lib/hooks/useToast';
import { ToastContainer } from '@/components/common/Toast';
import { LargeTitleHeader } from '@/components/common';
import { CatalogPriceUpdates } from '@/components/settings/CatalogPriceUpdates';

type TabId = 'general' | 'scheduling' | 'jobs' | 'financial' | 'quotes' | 'materials' | 'notifications' | 'company' | 'agreements' | 'integrations' | 'invites';

// Reusable Components
const SettingsCard = ({
  title,
  description,
  children
}: {
  title: string;
  description?: string;
  children: React.ReactNode
}) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
    <div className="p-6 border-b border-gray-200">
      <h3 className="text-gray-900 text-lg font-bold leading-tight tracking-[-0.015em]">
        {title}
      </h3>
      {description && (
        <p className="text-gray-600 text-sm mt-1">{description}</p>
      )}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const Toggle = ({
  checked,
  onChange,
  label,
  description
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string
}) => (
  <div className="flex items-center justify-between">
    <div>
      <p className="text-sm font-medium text-gray-900">{label}</p>
      {description && <p className="text-xs text-gray-600">{description}</p>}
    </div>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
    </label>
  </div>
);

const InputField = ({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  helpText,
  min,
  max,
  step,
  disabled,
  prefix,
  suffix,
}: {
  label: string;
  type?: string;
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  helpText?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  prefix?: string;
  suffix?: string;
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-900 mb-1">{label}</label>
    <div className="relative">
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{prefix}</span>
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        className={`w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 ${prefix ? 'pl-8' : ''} ${suffix ? 'pr-12' : ''}`}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">{suffix}</span>
      )}
    </div>
    {helpText && <p className="text-xs text-gray-600 mt-1">{helpText}</p>}
  </div>
);

const SelectField = ({
  label,
  value,
  onChange,
  options,
  helpText,
  disabled,
}: {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  options: { value: string | number; label: string }[];
  helpText?: string;
  disabled?: boolean;
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-900 mb-1">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    {helpText && <p className="text-xs text-gray-600 mt-1">{helpText}</p>}
  </div>
);

const CheckboxField = ({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) => (
  <label className={`flex items-center gap-3 text-sm text-gray-900 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
    />
    {label}
  </label>
);

const InfoBox = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
    <div className="flex items-start gap-3">
      <span className="material-symbols-outlined text-primary text-xl mt-0.5">info</span>
      <div className="text-sm text-gray-700">{children}</div>
    </div>
  </div>
);

export const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [settings, setSettings] = useState<Settings>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const { toast, toasts, removeToast } = useToast();
  const navigate = useNavigate();
  // Desktop Mode kill switch (#133) — local preference, not a server setting.
  const [desktopModeOn, setDesktopModeOn] = useDesktopModeSetting();

  const tabs: { id: TabId; label: string; icon: string }[] = [
    { id: 'general', label: 'General', icon: 'settings' },
    { id: 'scheduling', label: 'Scheduling', icon: 'calendar_month' },
    { id: 'jobs', label: 'Jobs', icon: 'work' },
    { id: 'financial', label: 'Financial', icon: 'payments' },
    { id: 'quotes', label: 'Quotes', icon: 'request_quote' },
    { id: 'materials', label: 'Materials', icon: 'inventory_2' },
    { id: 'notifications', label: 'Notifications', icon: 'notifications' },
    { id: 'company', label: 'Company', icon: 'business' },
    { id: 'agreements', label: 'Agreements', icon: 'handshake' },
    { id: 'integrations', label: 'Integrations', icon: 'hub' },
    { id: 'invites', label: 'Invites', icon: 'send' },
  ];

  // Honor ?tab=<id> so the top-nav Settings dropdown can deep-link to a section.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tabs.some(t => t.id === tab)) setActiveTab(tab as TabId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getSettings();
      setSettings(data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateSettings(activeTab, (settings as Record<string, unknown>)[activeTab] || {});
      setHasChanges(false);
      setSaveSuccess(true);
      toast.success('Settings saved');
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    loadSettings();
    setHasChanges(false);
  };

  const updateSetting = (category: keyof Settings, key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
    setHasChanges(true);
  };

  const updateNestedSetting = (category: keyof Settings, parent: string, key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [parent]: {
          ...(prev[category] as any)?.[parent],
          [key]: value
        }
      }
    }));
    setHasChanges(true);
  };

  // Common timezones
  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
    { value: 'America/Toronto', label: 'Eastern Time - Canada' },
    { value: 'America/Vancouver', label: 'Pacific Time - Canada' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
    { value: 'Europe/Paris', label: 'Central European Time (CET)' },
  ];

  return (
    <MainLayout>
      <div className="flex flex-col gap-6">
        {/* iOS Large Title Header */}
        <LargeTitleHeader
          title="Settings"
          subtitle="Configure preferences &amp; business rules"
          actions={saveSuccess ? (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-medium">
              <span className="material-symbols-outlined text-base">check_circle</span>
              <span className="hidden sm:inline">Saved</span>
            </div>
          ) : undefined}
        />

        {/* Tabs */}
        <div className="pb-2">
          <div className="flex border-b border-gray-200 gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-[3px] whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-b-primary text-primary'
                    : 'border-b-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="material-symbols-outlined text-lg">{tab.icon}</span>
                <span className="text-sm font-bold leading-normal tracking-[0.015em]">
                  {tab.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {/* ==================== GENERAL TAB ==================== */}
            {activeTab === 'general' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Units & Localization */}
                <SettingsCard title="Units & Localization" description="Configure measurement units and regional preferences">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Measurement System</label>
                      <div className="flex h-10 w-full items-center justify-center rounded-lg bg-gray-100 p-1">
                        {['imperial', 'metric'].map((unit) => (
                          <label key={unit} className="flex cursor-pointer h-full grow items-center justify-center overflow-hidden rounded-md px-4 has-[:checked]:bg-white has-[:checked]:shadow-sm text-gray-600 has-[:checked]:text-gray-900 text-sm font-medium leading-normal capitalize">
                            <span className="truncate">{unit}</span>
                            <input
                              type="radio"
                              name="units"
                              value={unit}
                              checked={settings.general?.units === unit}
                              onChange={(e) => updateSetting('general', 'units', e.target.value)}
                              className="invisible w-0"
                            />
                          </label>
                        ))}
                      </div>
                    </div>

                    <SelectField
                      label="Date Format"
                      value={settings.general?.dateFormat || 'MM/DD/YYYY'}
                      onChange={(v) => updateSetting('general', 'dateFormat', v)}
                      options={[
                        { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2024)' },
                        { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2024)' },
                        { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-12-31)' },
                      ]}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Time Format</label>
                      <div className="flex h-10 w-full items-center justify-center rounded-lg bg-gray-100 p-1">
                        {[{ value: '12h', label: '12-hour' }, { value: '24h', label: '24-hour' }].map((fmt) => (
                          <label key={fmt.value} className="flex cursor-pointer h-full grow items-center justify-center overflow-hidden rounded-md px-4 has-[:checked]:bg-white has-[:checked]:shadow-sm text-gray-600 has-[:checked]:text-gray-900 text-sm font-medium leading-normal">
                            <span className="truncate">{fmt.label}</span>
                            <input
                              type="radio"
                              name="timeFormat"
                              value={fmt.value}
                              checked={settings.general?.timeFormat === fmt.value}
                              onChange={(e) => updateSetting('general', 'timeFormat', e.target.value)}
                              className="invisible w-0"
                            />
                          </label>
                        ))}
                      </div>
                    </div>

                    <SelectField
                      label="Timezone"
                      value={settings.general?.timezone || 'America/New_York'}
                      onChange={(v) => updateSetting('general', 'timezone', v)}
                      options={timezones}
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Currency"
                        value={settings.general?.currency || 'CAD'}
                        onChange={(v) => updateSetting('general', 'currency', v)}
                        placeholder="CAD"
                      />
                      <InputField
                        label="Currency Symbol"
                        value={settings.general?.currencySymbol || '$'}
                        onChange={(v) => updateSetting('general', 'currencySymbol', v)}
                        placeholder="$"
                      />
                    </div>
                  </div>
                </SettingsCard>

                {/* Working Hours */}
                <SettingsCard title="Working Hours" description="Set default business hours for scheduling">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">Start Time</label>
                        <input
                          type="time"
                          value={settings.general?.workingHours?.start || '08:00'}
                          onChange={(e) => updateNestedSetting('general', 'workingHours', 'start', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">End Time</label>
                        <input
                          type="time"
                          value={settings.general?.workingHours?.end || '17:00'}
                          onChange={(e) => updateNestedSetting('general', 'workingHours', 'end', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Business Days</label>
                      <div className="flex flex-wrap gap-2">
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                          <label
                            key={day}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                              settings.general?.businessDays?.includes(day)
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={settings.general?.businessDays?.includes(day)}
                              onChange={(e) => {
                                const days = settings.general?.businessDays || [];
                                updateSetting('general', 'businessDays',
                                  e.target.checked
                                    ? [...days, day]
                                    : days.filter(d => d !== day)
                                );
                              }}
                              className="sr-only"
                            />
                            <span className="text-sm font-medium">{day.slice(0, 3)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </SettingsCard>

                {/* Overtime Rules */}
                <SettingsCard title="Overtime Rules" description="Configure automatic overtime calculations">
                  <div className="space-y-4">
                    <Toggle
                      checked={settings.general?.overtime?.enabled || false}
                      onChange={(checked) => updateNestedSetting('general', 'overtime', 'enabled', checked)}
                      label="Enable Overtime Calculations"
                      description="Automatically calculate overtime based on thresholds"
                    />

                    <div className={settings.general?.overtime?.enabled ? '' : 'opacity-50 pointer-events-none'}>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <InputField
                          label="Daily Threshold (hours)"
                          type="number"
                          value={settings.general?.overtime?.dailyThreshold || 8}
                          onChange={(v) => updateNestedSetting('general', 'overtime', 'dailyThreshold', Number(v))}
                          min={1}
                          max={24}
                          helpText="Hours before daily overtime kicks in"
                        />
                        <InputField
                          label="Weekly Threshold (hours)"
                          type="number"
                          value={settings.general?.overtime?.weeklyThreshold || 40}
                          onChange={(v) => updateNestedSetting('general', 'overtime', 'weeklyThreshold', Number(v))}
                          min={1}
                          max={168}
                          helpText="Hours before weekly overtime kicks in"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <InputField
                          label="Overtime Multiplier"
                          type="number"
                          value={settings.general?.overtime?.multiplier || 1.5}
                          onChange={(v) => updateNestedSetting('general', 'overtime', 'multiplier', Number(v))}
                          step={0.1}
                          min={1}
                          max={3}
                          suffix="x"
                        />
                        <InputField
                          label="Double Time Multiplier"
                          type="number"
                          value={settings.general?.overtime?.doubleTimeMultiplier || 2}
                          onChange={(v) => updateNestedSetting('general', 'overtime', 'doubleTimeMultiplier', Number(v))}
                          step={0.1}
                          min={1}
                          max={4}
                          suffix="x"
                          disabled={!settings.general?.overtime?.doubleTimeEnabled}
                        />
                      </div>

                      <div className="mt-4">
                        <CheckboxField
                          label="Enable double-time for holidays and extended overtime"
                          checked={settings.general?.overtime?.doubleTimeEnabled || false}
                          onChange={(checked) => updateNestedSetting('general', 'overtime', 'doubleTimeEnabled', checked)}
                        />
                      </div>
                    </div>
                  </div>
                </SettingsCard>

                {/* Union & Prevailing Wage */}
                <SettingsCard title="Union & Prevailing Wage" description="Enable support for union wage tables and prevailing wage requirements">
                  <Toggle
                    checked={settings.general?.unionWage || false}
                    onChange={(checked) => updateSetting('general', 'unionWage', checked)}
                    label="Enable Union Wage Support"
                    description="Use union wage tables for labor rate calculations"
                  />
                </SettingsCard>

                {/* Desktop Mode — trial feature (CR #133). Local to this browser
                    and applied instantly, so it is deliberately NOT part of the
                    server settings form or its Save button. */}
                <SettingsCard
                  title="Desktop Mode (trial)"
                  description="Wallpaper desktop with dashboard widgets, folders and shortcuts — main-menu items open as floating windows"
                >
                  <Toggle
                    checked={desktopModeOn}
                    onChange={(checked) => {
                      setDesktopModeOn(checked);
                      navigate(checked ? '/desktop' : '/dashboard');
                    }}
                    label="Enable Desktop Mode"
                    description="Takes effect immediately — no Save needed. Turn it off to go back to the classic layout exactly as it was."
                  />
                  <p className="mt-3 text-xs text-gray-500">
                    Stored in this browser only, and active on desktop-sized screens (768px+); phones keep the standard layout.
                  </p>
                </SettingsCard>
              </div>
            )}

            {/* ==================== SCHEDULING TAB ==================== */}
            {activeTab === 'scheduling' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Calendar Preferences */}
                <SettingsCard title="Calendar Preferences" description="Configure default calendar display options">
                  <div className="space-y-4">
                    <SelectField
                      label="Default Calendar View"
                      value={settings.scheduling?.defaultCalendarView || 'week'}
                      onChange={(v) => updateSetting('scheduling', 'defaultCalendarView', v)}
                      options={[
                        { value: 'day', label: 'Day View' },
                        { value: 'week', label: 'Week View' },
                        { value: 'month', label: 'Month View' },
                        { value: 'year', label: 'Year View' },
                        { value: 'gantt', label: 'Gantt Chart' },
                      ]}
                    />

                    <SelectField
                      label="Week Starts On"
                      value={settings.scheduling?.weekStartsOn || 'sunday'}
                      onChange={(v) => updateSetting('scheduling', 'weekStartsOn', v)}
                      options={[
                        { value: 'sunday', label: 'Sunday' },
                        { value: 'monday', label: 'Monday' },
                      ]}
                    />

                    <div className="space-y-3">
                      <CheckboxField
                        label="Show weekends on calendar"
                        checked={settings.scheduling?.showWeekends ?? true}
                        onChange={(checked) => updateSetting('scheduling', 'showWeekends', checked)}
                      />
                      <CheckboxField
                        label="Show week numbers"
                        checked={settings.scheduling?.showWeekNumbers || false}
                        onChange={(checked) => updateSetting('scheduling', 'showWeekNumbers', checked)}
                      />
                      <CheckboxField
                        label="Color code by job status"
                        checked={settings.scheduling?.colorByStatus ?? true}
                        onChange={(checked) => updateSetting('scheduling', 'colorByStatus', checked)}
                      />
                      <CheckboxField
                        label="Color code by crew"
                        checked={settings.scheduling?.colorByCrew || false}
                        onChange={(checked) => updateSetting('scheduling', 'colorByCrew', checked)}
                      />
                    </div>
                  </div>
                </SettingsCard>

                {/* Crew Capacity */}
                <SettingsCard title="Crew Capacity" description="Configure capacity planning parameters">
                  <div className="space-y-4">
                    <InputField
                      label="Standard Hours per Week"
                      type="number"
                      value={settings.scheduling?.standardHoursPerWeek || 40}
                      onChange={(v) => updateSetting('scheduling', 'standardHoursPerWeek', Number(v))}
                      min={1}
                      max={168}
                      helpText="Normal working hours per crew member per week"
                    />

                    <InputField
                      label="Max Overtime Hours per Week"
                      type="number"
                      value={settings.scheduling?.maxOvertimeHoursPerWeek || 20}
                      onChange={(v) => updateSetting('scheduling', 'maxOvertimeHoursPerWeek', Number(v))}
                      min={0}
                      max={80}
                      helpText="Maximum overtime hours allowed per crew member"
                    />

                    <InputField
                      label="Default Schedule Duration (days)"
                      type="number"
                      value={settings.scheduling?.defaultScheduleDuration || 1}
                      onChange={(v) => updateSetting('scheduling', 'defaultScheduleDuration', Number(v))}
                      min={1}
                      max={365}
                      helpText="Default duration when creating new schedule entries"
                    />
                  </div>
                </SettingsCard>

                {/* Scheduling Rules */}
                <SettingsCard title="Scheduling Rules" description="Configure scheduling behavior and requirements">
                  <div className="space-y-4">
                    <Toggle
                      checked={settings.scheduling?.allowDoubleBooking || false}
                      onChange={(checked) => updateSetting('scheduling', 'allowDoubleBooking', checked)}
                      label="Allow Double Booking"
                      description="Allow crews to be scheduled for multiple jobs at the same time"
                    />

                    <Toggle
                      checked={settings.scheduling?.requireCrewAssignment ?? true}
                      onChange={(checked) => updateSetting('scheduling', 'requireCrewAssignment', checked)}
                      label="Require Crew Assignment"
                      description="Jobs must have a crew assigned before scheduling"
                    />

                    <Toggle
                      checked={settings.scheduling?.autoAssignProjectManager || false}
                      onChange={(checked) => updateSetting('scheduling', 'autoAssignProjectManager', checked)}
                      label="Auto-assign Project Manager"
                      description="Automatically assign a project manager to new jobs"
                    />

                    <Toggle
                      checked={settings.scheduling?.observeStatutoryHolidays ?? true}
                      onChange={(checked) => updateSetting('scheduling', 'observeStatutoryHolidays', checked)}
                      label="Observe Statutory Holidays"
                      description="Exclude statutory holidays from scheduling calculations"
                    />
                  </div>
                </SettingsCard>

                {/* Holidays */}
                <SettingsCard title="Company Holidays" description="Manage company-observed holidays">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      {(settings.scheduling?.holidays || []).map((holiday, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <span className="material-symbols-outlined text-primary">event</span>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{holiday.name}</p>
                            <p className="text-xs text-gray-600">
                              {holiday.date} {holiday.recurring && '(Recurring)'}
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              const holidays = [...(settings.scheduling?.holidays || [])];
                              holidays.splice(idx, 1);
                              updateSetting('scheduling', 'holidays', holidays);
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <span className="material-symbols-outlined text-lg">delete</span>
                          </button>
                        </div>
                      ))}

                      {(settings.scheduling?.holidays?.length || 0) === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">No holidays configured</p>
                      )}
                    </div>

                    <button
                      onClick={() => {
                        const name = prompt('Holiday name:');
                        const date = prompt('Date (MM/DD or YYYY-MM-DD):');
                        const recurring = confirm('Is this a recurring annual holiday?');
                        if (name && date) {
                          const holidays = [...(settings.scheduling?.holidays || [])];
                          holidays.push({ name, date, recurring });
                          updateSetting('scheduling', 'holidays', holidays);
                        }
                      }}
                      className="w-full py-2 px-4 border-2 border-dashed border-gray-200 rounded-lg text-gray-600 hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
                    >
                      <span className="material-symbols-outlined">add</span>
                      Add Holiday
                    </button>
                  </div>
                </SettingsCard>
              </div>
            )}

            {/* ==================== JOBS TAB ==================== */}
            {activeTab === 'jobs' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Job Numbering */}
                <SettingsCard title="Job Numbering" description="Configure how job numbers are generated">
                  <div className="space-y-4">
                    <InputField
                      label="Job Number Prefix"
                      value={settings.jobs?.jobNumberPrefix || 'JOB-'}
                      onChange={(v) => updateSetting('jobs', 'jobNumberPrefix', v)}
                      placeholder="JOB-"
                      helpText="Prefix for all job numbers (e.g., JOB-, PRJ-, WO-)"
                    />

                    <SelectField
                      label="Numbering Format"
                      value={settings.jobs?.jobNumberFormat || 'sequential'}
                      onChange={(v) => updateSetting('jobs', 'jobNumberFormat', v)}
                      options={[
                        { value: 'sequential', label: 'Sequential (JOB-1001, JOB-1002...)' },
                        { value: 'year-sequential', label: 'Year + Sequential (JOB-2024-001)' },
                        { value: 'date-based', label: 'Date-based (JOB-20241231-01)' },
                      ]}
                    />

                    <InputField
                      label="Next Job Number"
                      type="number"
                      value={settings.jobs?.nextJobNumber || 1001}
                      onChange={(v) => updateSetting('jobs', 'nextJobNumber', Number(v))}
                      min={1}
                      helpText="The next job will use this number"
                    />

                    <InputField
                      label="Separator Character"
                      value={settings.jobs?.jobNumberSeparator || '-'}
                      onChange={(v) => updateSetting('jobs', 'jobNumberSeparator', v)}
                      placeholder="-"
                      helpText="Character between prefix and number"
                    />

                    <InfoBox>
                      <p className="font-medium mb-1">Preview:</p>
                      <code className="text-primary">
                        {settings.jobs?.jobNumberPrefix || 'JOB-'}
                        {settings.jobs?.jobNumberFormat === 'year-sequential' && '2024-'}
                        {settings.jobs?.jobNumberFormat === 'date-based' && '20241231-'}
                        {String(settings.jobs?.nextJobNumber || 1001).padStart(4, '0')}
                      </code>
                    </InfoBox>
                  </div>
                </SettingsCard>

                {/* Job Defaults */}
                <SettingsCard title="Job Defaults" description="Set default values for new jobs">
                  <div className="space-y-4">
                    <InputField
                      label="Default Duration (days)"
                      type="number"
                      value={settings.jobs?.defaultDuration || 5}
                      onChange={(v) => updateSetting('jobs', 'defaultDuration', Number(v))}
                      min={1}
                      max={365}
                      helpText="Default estimated duration for new jobs"
                    />

                    <InputField
                      label="Default Job Type"
                      value={settings.jobs?.defaultJobType || 'General'}
                      onChange={(v) => updateSetting('jobs', 'defaultJobType', v)}
                      placeholder="General"
                    />
                  </div>
                </SettingsCard>

                {/* Budget & Alerts */}
                <SettingsCard title="Budget & Alerts" description="Configure budget tracking and alert thresholds">
                  <div className="space-y-4">
                    <Toggle
                      checked={settings.jobs?.enableBudgetAlerts ?? true}
                      onChange={(checked) => updateSetting('jobs', 'enableBudgetAlerts', checked)}
                      label="Enable Budget Alerts"
                      description="Show warnings when jobs approach or exceed budget"
                    />

                    <InputField
                      label="Budget Alert Threshold"
                      type="number"
                      value={settings.jobs?.budgetAlertThreshold || 80}
                      onChange={(v) => updateSetting('jobs', 'budgetAlertThreshold', Number(v))}
                      min={1}
                      max={100}
                      suffix="%"
                      helpText="Alert when job costs reach this percentage of budget"
                      disabled={!settings.jobs?.enableBudgetAlerts}
                    />

                    <CheckboxField
                      label="Auto-create budget from accepted estimate"
                      checked={settings.jobs?.autoCreateBudgetFromEstimate || false}
                      onChange={(checked) => updateSetting('jobs', 'autoCreateBudgetFromEstimate', checked)}
                    />
                  </div>
                </SettingsCard>

                {/* Job Requirements */}
                <SettingsCard title="Job Requirements" description="Configure required tracking for jobs">
                  <div className="space-y-4">
                    <Toggle
                      checked={settings.jobs?.requireMaterialTracking || false}
                      onChange={(checked) => updateSetting('jobs', 'requireMaterialTracking', checked)}
                      label="Require Material Tracking"
                      description="Materials must be logged for each job"
                    />

                    <Toggle
                      checked={settings.jobs?.requireDailyLogs || false}
                      onChange={(checked) => updateSetting('jobs', 'requireDailyLogs', checked)}
                      label="Require Daily Logs"
                      description="Daily progress logs must be submitted"
                    />

                    <Toggle
                      checked={settings.jobs?.requirePhotos || false}
                      onChange={(checked) => updateSetting('jobs', 'requirePhotos', checked)}
                      label="Require Progress Photos"
                      description="Photos must be uploaded before job completion"
                    />

                    <Toggle
                      checked={settings.jobs?.requireApprovalToComplete || false}
                      onChange={(checked) => updateSetting('jobs', 'requireApprovalToComplete', checked)}
                      label="Require Approval to Complete"
                      description="Jobs must be approved by a manager before marking complete"
                    />
                  </div>
                </SettingsCard>

                {/* Job Types */}
                <SettingsCard title="Job Types" description="Manage job categories and types">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      {(settings.jobs?.jobTypes || [
                        { name: 'New Construction', color: '#3B82F6' },
                        { name: 'Renovation', color: '#10B981' },
                        { name: 'Repair', color: '#F59E0B' },
                        { name: 'Maintenance', color: '#6B7280' },
                      ]).map((jt, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: jt.color }}
                          />
                          <span className="flex-1 text-sm font-medium text-gray-900">{jt.name}</span>
                          <button
                            onClick={() => {
                              const types = [...(settings.jobs?.jobTypes || [])];
                              types.splice(idx, 1);
                              updateSetting('jobs', 'jobTypes', types);
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <span className="material-symbols-outlined text-lg">delete</span>
                          </button>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={() => {
                        const name = prompt('Job type name:');
                        const color = prompt('Color (hex, e.g., #3B82F6):', '#3B82F6');
                        if (name && color) {
                          const types = [...(settings.jobs?.jobTypes || [])];
                          types.push({ name, color });
                          updateSetting('jobs', 'jobTypes', types);
                        }
                      }}
                      className="w-full py-2 px-4 border-2 border-dashed border-gray-200 rounded-lg text-gray-600 hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
                    >
                      <span className="material-symbols-outlined">add</span>
                      Add Job Type
                    </button>
                  </div>
                </SettingsCard>

                {/* Status Workflow */}
                <SettingsCard title="Status Workflow" description="Configure job status transitions">
                  <div className="space-y-4">
                    <CheckboxField
                      label="Allow skipping statuses"
                      checked={settings.jobs?.allowSkipStatuses || false}
                      onChange={(checked) => updateSetting('jobs', 'allowSkipStatuses', checked)}
                    />

                    <InfoBox>
                      <p className="font-medium mb-2">Default Job Status Flow:</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        {['Draft', 'Quoted', 'Scheduled', 'In Progress', 'Completed'].map((status, idx, arr) => (
                          <span key={status} className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-gray-100 rounded">{status}</span>
                            {idx < arr.length - 1 && <span className="material-symbols-outlined text-gray-400">arrow_forward</span>}
                          </span>
                        ))}
                      </div>
                    </InfoBox>
                  </div>
                </SettingsCard>
              </div>
            )}

            {/* ==================== FINANCIAL TAB ==================== */}
            {activeTab === 'financial' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Online card payments (Stripe) — kill switch, server-enforced */}
                <SettingsCard title="Online Card Payments" description="The Stripe 'Pay now by card' buttons on client invoices">
                  <div className="space-y-2">
                    <Toggle
                      checked={settings.financial?.stripePaymentsEnabled !== false}
                      onChange={(v) => updateSetting('financial', 'stripePaymentsEnabled', v)}
                      label="Accept card payments online"
                      description="Off hides the pay buttons in the client portal and blocks new payment links server-side. Card fees: 2.9% + 30¢. e-Transfer instructions on invoices are unaffected."
                    />
                  </div>
                </SettingsCard>
                {/* Tax Rates */}
                <SettingsCard title="Tax Configuration" description="Configure tax rates for quotes and invoices">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Sales Tax Rate"
                        type="number"
                        value={settings.financial?.salesTaxRate || 0}
                        onChange={(v) => updateSetting('financial', 'salesTaxRate', parseFloat(v) || 0)}
                        step={0.01}
                        min={0}
                        max={100}
                        suffix="%"
                      />
                      <InputField
                        label="Use Tax Rate"
                        type="number"
                        value={settings.financial?.useTaxRate || 0}
                        onChange={(v) => updateSetting('financial', 'useTaxRate', parseFloat(v) || 0)}
                        step={0.01}
                        min={0}
                        max={100}
                        suffix="%"
                      />
                    </div>

                    <CheckboxField
                      label="Tax included in quoted prices"
                      checked={settings.financial?.taxIncludedInPrice || false}
                      onChange={(checked) => updateSetting('financial', 'taxIncludedInPrice', checked)}
                    />

                    <CheckboxField
                      label="Calculate tax by location"
                      checked={settings.financial?.taxByLocation || false}
                      onChange={(checked) => updateSetting('financial', 'taxByLocation', checked)}
                    />
                  </div>
                </SettingsCard>

                {/* Invoice Settings */}
                <SettingsCard title="Invoice Settings" description="Configure invoice numbering and payment terms">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Invoice Prefix"
                        value={settings.financial?.invoicePrefix || 'INV-'}
                        onChange={(v) => updateSetting('financial', 'invoicePrefix', v)}
                        placeholder="INV-"
                      />
                      <InputField
                        label="Next Invoice Number"
                        type="number"
                        value={settings.financial?.nextInvoiceNumber || 1000}
                        onChange={(v) => updateSetting('financial', 'nextInvoiceNumber', parseInt(v) || 1)}
                        min={1}
                      />
                    </div>

                    <SelectField
                      label="Default Payment Terms"
                      value={settings.financial?.defaultPaymentTerms || 30}
                      onChange={(v) => updateSetting('financial', 'defaultPaymentTerms', parseInt(v))}
                      options={[
                        { value: 0, label: 'Due on Receipt' },
                        { value: 15, label: 'Net 15' },
                        { value: 30, label: 'Net 30' },
                        { value: 45, label: 'Net 45' },
                        { value: 60, label: 'Net 60' },
                        { value: 90, label: 'Net 90' },
                      ]}
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Late Fee"
                        type="number"
                        value={settings.financial?.lateFeePercentage || 0}
                        onChange={(v) => updateSetting('financial', 'lateFeePercentage', parseFloat(v) || 0)}
                        step={0.1}
                        min={0}
                        max={100}
                        suffix="%"
                      />
                      <InputField
                        label="Grace Period (days)"
                        type="number"
                        value={settings.financial?.lateFeeGracePeriod || 0}
                        onChange={(v) => updateSetting('financial', 'lateFeeGracePeriod', parseInt(v) || 0)}
                        min={0}
                        max={90}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">Default Invoice Notes</label>
                      <textarea
                        value={settings.financial?.defaultInvoiceNotes || ''}
                        onChange={(e) => updateSetting('financial', 'defaultInvoiceNotes', e.target.value)}
                        placeholder="Thank you for your business!"
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">Invoice Footer (PDF/Print)</label>
                      <textarea
                        value={settings.company?.invoiceFooter || ''}
                        onChange={(e) => updateSetting('company', 'invoiceFooter', e.target.value)}
                        placeholder="Thank you for your business! Please make payment by the due date."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <p className="text-xs text-gray-500 mt-1">This text appears at the bottom of printed invoices and PDF exports.</p>
                    </div>

                    <CheckboxField
                      label="Reset invoice numbers annually"
                      checked={settings.financial?.resetInvoiceNumberAnnually || false}
                      onChange={(checked) => updateSetting('financial', 'resetInvoiceNumberAnnually', checked)}
                    />
                  </div>
                </SettingsCard>

                {/* Payment Methods */}
                <SettingsCard title="Payment Methods" description="Configure accepted payment methods">
                  <div className="space-y-4">
                    {[
                      { id: 'cash', name: 'Cash' },
                      { id: 'check', name: 'Check' },
                      { id: 'credit', name: 'Credit Card' },
                      { id: 'debit', name: 'Debit Card' },
                      { id: 'ach', name: 'ACH / Bank Transfer' },
                      { id: 'etransfer', name: 'E-Transfer' },
                    ].map((method) => {
                      const methods = settings.financial?.paymentMethods || [];
                      const isEnabled = methods.find(m => m.id === method.id)?.enabled ?? true;
                      return (
                        <div key={method.id} className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">{method.name}</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isEnabled}
                              onChange={(e) => {
                                const existing = methods.find(m => m.id === method.id);
                                const updated = methods.filter(m => m.id !== method.id);
                                updated.push({
                                  id: method.id,
                                  name: method.name,
                                  enabled: e.target.checked,
                                  instructions: existing?.instructions,
                                });
                                updateSetting('financial', 'paymentMethods', updated);
                              }}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </SettingsCard>

                {/* Profit Margins */}
                <SettingsCard title="Profit Margin Targets" description="Set target profit margins for different types of work">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Labor Margin"
                        type="number"
                        value={settings.financial?.laborMargin || 0}
                        onChange={(v) => updateSetting('financial', 'laborMargin', parseFloat(v) || 0)}
                        min={0}
                        max={100}
                        suffix="%"
                      />
                      <InputField
                        label="Materials Margin"
                        type="number"
                        value={settings.financial?.materialsMargin || 0}
                        onChange={(v) => updateSetting('financial', 'materialsMargin', parseFloat(v) || 0)}
                        min={0}
                        max={100}
                        suffix="%"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Equipment Margin"
                        type="number"
                        value={settings.financial?.equipmentMargin || 0}
                        onChange={(v) => updateSetting('financial', 'equipmentMargin', parseFloat(v) || 0)}
                        min={0}
                        max={100}
                        suffix="%"
                      />
                      <InputField
                        label="Subcontractor Margin"
                        type="number"
                        value={settings.financial?.subcontractorMargin || 0}
                        onChange={(v) => updateSetting('financial', 'subcontractorMargin', parseFloat(v) || 0)}
                        min={0}
                        max={100}
                        suffix="%"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Overhead Rate"
                        type="number"
                        value={settings.financial?.overheadRate || 0}
                        onChange={(v) => updateSetting('financial', 'overheadRate', parseFloat(v) || 0)}
                        min={0}
                        max={100}
                        suffix="%"
                      />
                      <InputField
                        label="Contingency Rate"
                        type="number"
                        value={settings.financial?.contingencyRate || 0}
                        onChange={(v) => updateSetting('financial', 'contingencyRate', parseFloat(v) || 0)}
                        min={0}
                        max={50}
                        suffix="%"
                        helpText="Added to quotes for unexpected costs"
                      />
                    </div>
                  </div>
                </SettingsCard>

                {/* Automation */}
                <SettingsCard title="Invoice Automation" description="Configure automatic invoice actions">
                  <div className="space-y-4">
                    <Toggle
                      checked={settings.financial?.autoSendInvoiceOnCompletion || false}
                      onChange={(checked) => updateSetting('financial', 'autoSendInvoiceOnCompletion', checked)}
                      label="Auto-send Invoice on Job Completion"
                      description="Automatically send invoice when job is marked complete"
                    />

                    <Toggle
                      checked={settings.financial?.autoMarkOverdue ?? true}
                      onChange={(checked) => updateSetting('financial', 'autoMarkOverdue', checked)}
                      label="Auto-mark Overdue"
                      description="Automatically mark invoices as overdue past due date"
                    />

                    <Toggle
                      checked={settings.financial?.sendPaymentReminders || false}
                      onChange={(checked) => updateSetting('financial', 'sendPaymentReminders', checked)}
                      label="Send Payment Reminders"
                      description="Send automatic payment reminder emails"
                    />

                    <InputField
                      label="Reminder Days Before Due"
                      type="number"
                      value={settings.financial?.reminderDaysBefore || 3}
                      onChange={(v) => updateSetting('financial', 'reminderDaysBefore', parseInt(v) || 3)}
                      min={1}
                      max={30}
                      disabled={!settings.financial?.sendPaymentReminders}
                    />
                  </div>
                </SettingsCard>

                {/* Bank Info */}
                <SettingsCard title="Bank Information" description="Bank details for invoice payments (optional)">
                  <div className="space-y-4">
                    <InputField
                      label="Bank Name"
                      value={settings.financial?.bankName || ''}
                      onChange={(v) => updateSetting('financial', 'bankName', v)}
                      placeholder="First National Bank"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Account Number"
                        value={settings.financial?.bankAccountNumber || ''}
                        onChange={(v) => updateSetting('financial', 'bankAccountNumber', v)}
                        placeholder="****1234"
                      />
                      <InputField
                        label="Routing Number"
                        value={settings.financial?.bankRoutingNumber || ''}
                        onChange={(v) => updateSetting('financial', 'bankRoutingNumber', v)}
                        placeholder="123456789"
                      />
                    </div>
                    <CheckboxField
                      label="Accept credit card payments"
                      checked={settings.financial?.acceptCreditCards || false}
                      onChange={(checked) => updateSetting('financial', 'acceptCreditCards', checked)}
                    />
                  </div>
                </SettingsCard>
              </div>
            )}

            {/* ==================== QUOTES TAB ==================== */}
            {activeTab === 'quotes' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Quote Numbering */}
                <SettingsCard title="Quote Numbering" description="Configure how quote numbers are generated">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Quote Prefix"
                        value={settings.quotes?.quotePrefix || 'QT-'}
                        onChange={(v) => updateSetting('quotes', 'quotePrefix', v)}
                        placeholder="QT-"
                      />
                      <InputField
                        label="Next Quote Number"
                        type="number"
                        value={settings.quotes?.nextQuoteNumber || 1000}
                        onChange={(v) => updateSetting('quotes', 'nextQuoteNumber', parseInt(v) || 1)}
                        min={1}
                      />
                    </div>

                    <InputField
                      label="Number Padding"
                      type="number"
                      value={settings.quotes?.quoteNumberPadding || 4}
                      onChange={(v) => updateSetting('quotes', 'quoteNumberPadding', parseInt(v) || 4)}
                      min={1}
                      max={10}
                      helpText="Zero-pad quote numbers (e.g., 4 = QT-0001)"
                    />

                    <InfoBox>
                      <p className="font-medium mb-1">Preview:</p>
                      <code className="text-primary">
                        {settings.quotes?.quotePrefix || 'QT-'}
                        {String(settings.quotes?.nextQuoteNumber || 1000).padStart(settings.quotes?.quoteNumberPadding || 4, '0')}
                      </code>
                    </InfoBox>
                  </div>
                </SettingsCard>

                {/* Change Order Numbering */}
                <SettingsCard title="Change Order Numbering" description="Configure change order numbers">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Change Order Prefix"
                        value={settings.quotes?.changeOrderPrefix || 'CO-'}
                        onChange={(v) => updateSetting('quotes', 'changeOrderPrefix', v)}
                        placeholder="CO-"
                      />
                      <InputField
                        label="Next CO Number"
                        type="number"
                        value={settings.quotes?.nextChangeOrderNumber || 1}
                        onChange={(v) => updateSetting('quotes', 'nextChangeOrderNumber', parseInt(v) || 1)}
                        min={1}
                      />
                    </div>
                  </div>
                </SettingsCard>

                {/* Quote Settings */}
                <SettingsCard title="Quote Settings" description="Configure quote behavior and defaults">
                  <div className="space-y-4">
                    <InputField
                      label="Quote Expiration (days)"
                      type="number"
                      value={settings.quotes?.quoteExpirationDays || 30}
                      onChange={(v) => updateSetting('quotes', 'quoteExpirationDays', parseInt(v) || 30)}
                      min={1}
                      max={365}
                      helpText="Days until quotes expire"
                    />

                    <Toggle
                      checked={settings.quotes?.autoExpireQuotes ?? true}
                      onChange={(checked) => updateSetting('quotes', 'autoExpireQuotes', checked)}
                      label="Auto-expire Quotes"
                      description="Automatically mark quotes as expired past expiration date"
                    />

                    <Toggle
                      checked={settings.quotes?.requireSignature || false}
                      onChange={(checked) => updateSetting('quotes', 'requireSignature', checked)}
                      label="Require Client Signature"
                      description="Quotes must be signed before acceptance"
                    />

                    <Toggle
                      checked={settings.quotes?.requireDeposit || false}
                      onChange={(checked) => updateSetting('quotes', 'requireDeposit', checked)}
                      label="Require Deposit"
                      description="Require a deposit before starting work"
                    />

                    <InputField
                      label="Default Deposit"
                      type="number"
                      value={settings.quotes?.defaultDepositPercent || 0}
                      onChange={(v) => updateSetting('quotes', 'defaultDepositPercent', parseFloat(v) || 0)}
                      min={0}
                      max={100}
                      suffix="%"
                      disabled={!settings.quotes?.requireDeposit}
                    />
                  </div>
                </SettingsCard>

                {/* Approval Workflow */}
                <SettingsCard title="Approval Workflow" description="Configure quote approval requirements">
                  <div className="space-y-4">
                    <Toggle
                      checked={settings.quotes?.requireApproval || false}
                      onChange={(checked) => updateSetting('quotes', 'requireApproval', checked)}
                      label="Require Manager Approval"
                      description="Quotes above threshold require manager approval before sending"
                    />

                    <InputField
                      label="Approval Threshold"
                      type="number"
                      value={settings.quotes?.approvalThreshold || 5000}
                      onChange={(v) => updateSetting('quotes', 'approvalThreshold', parseFloat(v) || 0)}
                      min={0}
                      prefix="$"
                      helpText="Quotes above this amount require approval"
                      disabled={!settings.quotes?.requireApproval}
                    />
                  </div>
                </SettingsCard>

                {/* Display Options */}
                <SettingsCard title="Quote Display Options" description="Configure what's shown on quotes">
                  <div className="space-y-4">
                    <CheckboxField
                      label="Show cost breakdown by category"
                      checked={settings.quotes?.showCostBreakdown ?? true}
                      onChange={(checked) => updateSetting('quotes', 'showCostBreakdown', checked)}
                    />

                    <CheckboxField
                      label="Show margin percentages (internal only)"
                      checked={settings.quotes?.showMarginPercent || false}
                      onChange={(checked) => updateSetting('quotes', 'showMarginPercent', checked)}
                    />

                    <CheckboxField
                      label="Show unit prices on line items"
                      checked={settings.quotes?.showUnitPrices ?? true}
                      onChange={(checked) => updateSetting('quotes', 'showUnitPrices', checked)}
                    />
                  </div>
                </SettingsCard>

                {/* Terms & Conditions */}
                <SettingsCard title="Terms & Conditions" description="Default text for quotes">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">Default Quote Terms</label>
                      <textarea
                        value={settings.quotes?.defaultQuoteTerms || ''}
                        onChange={(e) => updateSetting('quotes', 'defaultQuoteTerms', e.target.value)}
                        placeholder="Enter your standard terms and conditions..."
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">Warranty Text</label>
                      <textarea
                        value={settings.quotes?.warrantyText || ''}
                        onChange={(e) => updateSetting('quotes', 'warrantyText', e.target.value)}
                        placeholder="Enter warranty information..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                </SettingsCard>
              </div>
            )}

            {/* ==================== MATERIALS TAB ==================== */}
            {activeTab === 'materials' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* CR #189 — receipt→catalog price sweep with approval queue */}
                <div className="lg:col-span-2">
                  <CatalogPriceUpdates />
                </div>
                {/* Labor Rates */}
                <SettingsCard title="Default Labor Rates" description="Set standard hourly rates for labor categories">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Foreman Rate"
                        type="number"
                        value={settings.materials?.foremanRate || 0}
                        onChange={(v) => updateSetting('materials', 'foremanRate', parseFloat(v) || 0)}
                        step={0.5}
                        min={0}
                        prefix="$"
                        suffix="/hr"
                      />
                      <InputField
                        label="Journeyman Rate"
                        type="number"
                        value={settings.materials?.journeymanRate || 0}
                        onChange={(v) => updateSetting('materials', 'journeymanRate', parseFloat(v) || 0)}
                        step={0.5}
                        min={0}
                        prefix="$"
                        suffix="/hr"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Apprentice Rate"
                        type="number"
                        value={settings.materials?.apprenticeRate || 0}
                        onChange={(v) => updateSetting('materials', 'apprenticeRate', parseFloat(v) || 0)}
                        step={0.5}
                        min={0}
                        prefix="$"
                        suffix="/hr"
                      />
                      <InputField
                        label="Helper Rate"
                        type="number"
                        value={settings.materials?.helperRate || 0}
                        onChange={(v) => updateSetting('materials', 'helperRate', parseFloat(v) || 0)}
                        step={0.5}
                        min={0}
                        prefix="$"
                        suffix="/hr"
                      />
                    </div>
                    <InputField
                      label="Labor Burden"
                      type="number"
                      value={settings.materials?.laborBurden || 0}
                      onChange={(v) => updateSetting('materials', 'laborBurden', parseFloat(v) || 0)}
                      min={0}
                      max={100}
                      suffix="%"
                      helpText="Covers taxes, insurance, benefits, etc."
                    />
                    <InputField
                      label="Minimum Labor Charge (hours)"
                      type="number"
                      value={settings.materials?.minimumLaborCharge || 0}
                      onChange={(v) => updateSetting('materials', 'minimumLaborCharge', parseFloat(v) || 0)}
                      step={0.5}
                      min={0}
                      helpText="Minimum billable hours per service call"
                    />
                  </div>
                </SettingsCard>

                {/* Material Markups */}
                <SettingsCard title="Material Markups" description="Configure default markups for material categories">
                  <div className="space-y-4">
                    <InputField
                      label="Default Material Markup"
                      type="number"
                      value={settings.materials?.defaultMaterialMarkup || 20}
                      onChange={(v) => updateSetting('materials', 'defaultMaterialMarkup', parseFloat(v) || 0)}
                      min={0}
                      max={500}
                      suffix="%"
                      helpText="Applied to materials without a specific category markup"
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Lumber Markup"
                        type="number"
                        value={settings.materials?.lumberMarkup || 0}
                        onChange={(v) => updateSetting('materials', 'lumberMarkup', parseFloat(v) || 0)}
                        min={0}
                        max={500}
                        suffix="%"
                      />
                      <InputField
                        label="Hardware Markup"
                        type="number"
                        value={settings.materials?.hardwareMarkup || 0}
                        onChange={(v) => updateSetting('materials', 'hardwareMarkup', parseFloat(v) || 0)}
                        min={0}
                        max={500}
                        suffix="%"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Fixtures Markup"
                        type="number"
                        value={settings.materials?.fixturesMarkup || 0}
                        onChange={(v) => updateSetting('materials', 'fixturesMarkup', parseFloat(v) || 0)}
                        min={0}
                        max={500}
                        suffix="%"
                      />
                      <InputField
                        label="Miscellaneous Markup"
                        type="number"
                        value={settings.materials?.miscMarkup || 0}
                        onChange={(v) => updateSetting('materials', 'miscMarkup', parseFloat(v) || 0)}
                        min={0}
                        max={500}
                        suffix="%"
                      />
                    </div>
                  </div>
                </SettingsCard>

                {/* Waste Factors */}
                <SettingsCard title="Waste Factors" description="Configure material waste calculations">
                  <div className="space-y-4">
                    <Toggle
                      checked={settings.materials?.enableWasteFactor ?? true}
                      onChange={(checked) => updateSetting('materials', 'enableWasteFactor', checked)}
                      label="Enable Waste Factor"
                      description="Automatically add waste percentage to material quantities"
                    />

                    <InputField
                      label="Default Waste"
                      type="number"
                      value={settings.materials?.defaultWastePercent || 10}
                      onChange={(v) => updateSetting('materials', 'defaultWastePercent', parseFloat(v) || 0)}
                      min={0}
                      max={50}
                      suffix="%"
                      disabled={!settings.materials?.enableWasteFactor}
                      helpText="Extra material to account for waste, cuts, etc."
                    />
                  </div>
                </SettingsCard>

                {/* Inventory Settings */}
                <SettingsCard title="Inventory Settings" description="Configure inventory management">
                  <div className="space-y-4">
                    <Toggle
                      checked={settings.materials?.enableLowStockAlerts ?? true}
                      onChange={(checked) => updateSetting('materials', 'enableLowStockAlerts', checked)}
                      label="Enable Low Stock Alerts"
                      description="Show alerts when inventory falls below threshold"
                    />

                    <InputField
                      label="Low Stock Threshold"
                      type="number"
                      value={settings.materials?.lowStockThreshold || 10}
                      onChange={(v) => updateSetting('materials', 'lowStockThreshold', parseInt(v) || 10)}
                      min={1}
                      disabled={!settings.materials?.enableLowStockAlerts}
                      helpText="Alert when stock falls below this quantity"
                    />

                    <Toggle
                      checked={settings.materials?.autoReorderEnabled || false}
                      onChange={(checked) => updateSetting('materials', 'autoReorderEnabled', checked)}
                      label="Auto-reorder"
                      description="Automatically create purchase orders for low stock items"
                    />

                    <InputField
                      label="Default Reorder Quantity"
                      type="number"
                      value={settings.materials?.defaultReorderQuantity || 50}
                      onChange={(v) => updateSetting('materials', 'defaultReorderQuantity', parseInt(v) || 50)}
                      min={1}
                      disabled={!settings.materials?.autoReorderEnabled}
                    />
                  </div>
                </SettingsCard>

                {/* Standard Assemblies */}
                <SettingsCard title="Standard Assemblies" description="Configure common assemblies for quick quoting">
                  <div className="space-y-4">
                    <Toggle
                      checked={settings.materials?.useStandardAssemblies || false}
                      onChange={(checked) => updateSetting('materials', 'useStandardAssemblies', checked)}
                      label="Enable Standard Assemblies"
                      description="Use pre-configured assemblies in quote builder"
                    />

                    <InfoBox>
                      Standard assemblies are pre-configured groups of materials and labor for common tasks.
                      Configure assemblies in the Catalog section.
                    </InfoBox>
                  </div>
                </SettingsCard>
              </div>
            )}

            {/* ==================== NOTIFICATIONS TAB ==================== */}
            {activeTab === 'notifications' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Email Notifications */}
                <SettingsCard title="Email Notifications" description="Configure email alert settings">
                  <div className="space-y-4">
                    <Toggle
                      checked={settings.notifications?.emailNotifications ?? true}
                      onChange={(checked) => updateSetting('notifications', 'emailNotifications', checked)}
                      label="Enable Email Notifications"
                      description="Send email alerts for important events"
                    />

                    <div className={settings.notifications?.emailNotifications ? '' : 'opacity-50 pointer-events-none'}>
                      <div className="space-y-4 mt-4">
                        <InputField
                          label="From Address"
                          type="email"
                          value={settings.notifications?.emailFrom || ''}
                          onChange={(v) => updateSetting('notifications', 'emailFrom', v)}
                          placeholder="noreply@yourcompany.com"
                        />
                        <InputField
                          label="Reply-To Address"
                          type="email"
                          value={settings.notifications?.emailReplyTo || ''}
                          onChange={(v) => updateSetting('notifications', 'emailReplyTo', v)}
                          placeholder="info@yourcompany.com"
                        />
                      </div>
                    </div>
                  </div>
                </SettingsCard>

                {/* Event Triggers */}
                <SettingsCard title="Event Triggers" description="Choose which events trigger notifications">
                  <div className={`space-y-3 ${settings.notifications?.emailNotifications ? '' : 'opacity-50 pointer-events-none'}`}>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">Jobs</p>
                    <CheckboxField
                      label="New job created"
                      checked={settings.notifications?.notifyJobCreated || false}
                      onChange={(checked) => updateSetting('notifications', 'notifyJobCreated', checked)}
                    />
                    <CheckboxField
                      label="Job status changes"
                      checked={settings.notifications?.notifyJobStatus || false}
                      onChange={(checked) => updateSetting('notifications', 'notifyJobStatus', checked)}
                    />
                    <CheckboxField
                      label="Job completed"
                      checked={settings.notifications?.notifyJobCompleted || false}
                      onChange={(checked) => updateSetting('notifications', 'notifyJobCompleted', checked)}
                    />
                    <CheckboxField
                      label="Budget exceeded"
                      checked={settings.notifications?.notifyBudgetExceeded || false}
                      onChange={(checked) => updateSetting('notifications', 'notifyBudgetExceeded', checked)}
                    />

                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mt-4 mb-2">Quotes</p>
                    <CheckboxField
                      label="Quote sent"
                      checked={settings.notifications?.notifyQuoteSent || false}
                      onChange={(checked) => updateSetting('notifications', 'notifyQuoteSent', checked)}
                    />
                    <CheckboxField
                      label="Quote approved"
                      checked={settings.notifications?.notifyQuoteApproved || false}
                      onChange={(checked) => updateSetting('notifications', 'notifyQuoteApproved', checked)}
                    />
                    <CheckboxField
                      label="Quote expiring soon"
                      checked={settings.notifications?.notifyQuoteExpiring || false}
                      onChange={(checked) => updateSetting('notifications', 'notifyQuoteExpiring', checked)}
                    />

                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mt-4 mb-2">Billing</p>
                    <CheckboxField
                      label="Invoice sent"
                      checked={settings.notifications?.notifyInvoiceSent || false}
                      onChange={(checked) => updateSetting('notifications', 'notifyInvoiceSent', checked)}
                    />
                    <CheckboxField
                      label="Payment received"
                      checked={settings.notifications?.notifyPaymentReceived || false}
                      onChange={(checked) => updateSetting('notifications', 'notifyPaymentReceived', checked)}
                    />
                    <CheckboxField
                      label="Invoice overdue"
                      checked={settings.notifications?.notifyOverdue || false}
                      onChange={(checked) => updateSetting('notifications', 'notifyOverdue', checked)}
                    />

                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mt-4 mb-2">Operations</p>
                    <CheckboxField
                      label="Low stock alerts"
                      checked={settings.notifications?.notifyLowStock || false}
                      onChange={(checked) => updateSetting('notifications', 'notifyLowStock', checked)}
                    />
                    <CheckboxField
                      label="Schedule conflicts"
                      checked={settings.notifications?.notifyScheduleConflict || false}
                      onChange={(checked) => updateSetting('notifications', 'notifyScheduleConflict', checked)}
                    />
                    <CheckboxField
                      label="Crew assignment changes"
                      checked={settings.notifications?.notifyCrewAssignment || false}
                      onChange={(checked) => updateSetting('notifications', 'notifyCrewAssignment', checked)}
                    />
                  </div>
                </SettingsCard>

                {/* In-App Notifications */}
                <SettingsCard title="In-App Notifications" description="Configure in-application alerts">
                  <div className="space-y-4">
                    <Toggle
                      checked={settings.notifications?.inAppNotifications ?? true}
                      onChange={(checked) => updateSetting('notifications', 'inAppNotifications', checked)}
                      label="Enable In-App Notifications"
                      description="Show alerts within the application"
                    />

                    <div className={settings.notifications?.inAppNotifications ? '' : 'opacity-50 pointer-events-none'}>
                      <CheckboxField
                        label="Enable desktop notifications"
                        checked={settings.notifications?.desktopNotifications || false}
                        onChange={(checked) => updateSetting('notifications', 'desktopNotifications', checked)}
                      />
                      <CheckboxField
                        label="Enable notification sounds"
                        checked={settings.notifications?.soundEnabled || false}
                        onChange={(checked) => updateSetting('notifications', 'soundEnabled', checked)}
                      />
                    </div>
                  </div>
                </SettingsCard>

                {/* Quiet Hours */}
                <SettingsCard title="Quiet Hours" description="Set times when notifications are silenced">
                  <div className="space-y-4">
                    <Toggle
                      checked={settings.notifications?.quietHoursEnabled || false}
                      onChange={(checked) => updateSetting('notifications', 'quietHoursEnabled', checked)}
                      label="Enable Quiet Hours"
                      description="Silence notifications during specified hours"
                    />

                    <div className={settings.notifications?.quietHoursEnabled ? 'grid grid-cols-2 gap-4' : 'grid grid-cols-2 gap-4 opacity-50 pointer-events-none'}>
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">Start</label>
                        <input
                          type="time"
                          value={settings.notifications?.quietHoursStart || '22:00'}
                          onChange={(e) => updateSetting('notifications', 'quietHoursStart', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">End</label>
                        <input
                          type="time"
                          value={settings.notifications?.quietHoursEnd || '07:00'}
                          onChange={(e) => updateSetting('notifications', 'quietHoursEnd', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                  </div>
                </SettingsCard>

                {/* Summary Reports */}
                <SettingsCard title="Summary Reports" description="Configure automated report emails">
                  <div className="space-y-4">
                    <Toggle
                      checked={settings.notifications?.dailySummaryEmail || false}
                      onChange={(checked) => updateSetting('notifications', 'dailySummaryEmail', checked)}
                      label="Daily Summary Email"
                      description="Receive a daily summary of activities"
                    />

                    <Toggle
                      checked={settings.notifications?.weeklySummaryEmail || false}
                      onChange={(checked) => updateSetting('notifications', 'weeklySummaryEmail', checked)}
                      label="Weekly Summary Email"
                      description="Receive a weekly summary report"
                    />

                    <div className={settings.notifications?.dailySummaryEmail || settings.notifications?.weeklySummaryEmail ? '' : 'opacity-50 pointer-events-none'}>
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">Send Time</label>
                        <input
                          type="time"
                          value={settings.notifications?.summaryEmailTime || '08:00'}
                          onChange={(e) => updateSetting('notifications', 'summaryEmailTime', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                  </div>
                </SettingsCard>
              </div>
            )}

            {/* ==================== COMPANY TAB ==================== */}
            {activeTab === 'company' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Company Information */}
                <SettingsCard title="Company Information" description="Basic company details for documents">
                  <div className="space-y-4">
                    {/* Logo Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Company Logo</label>
                      <div className="flex items-center gap-4">
                        {settings.company?.logoUrl ? (
                          <div className="relative">
                            <img
                              src={settings.company.logoUrl}
                              alt="Company Logo"
                              className="h-20 w-20 object-contain rounded-lg border border-gray-200 bg-gray-50 p-2"
                            />
                            <button
                              onClick={() => updateSetting('company', 'logoUrl', '')}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                            >
                              <span className="material-symbols-outlined text-sm">close</span>
                            </button>
                          </div>
                        ) : (
                          <div className="h-20 w-20 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center">
                            <span className="material-symbols-outlined text-gray-400">image</span>
                          </div>
                        )}
                        <div className="flex-1">
                          <label className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 cursor-pointer transition">
                            <span className="material-symbols-outlined text-sm">upload</span>
                            <span className="text-sm font-medium">Upload Logo</span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    updateSetting('company', 'logoUrl', reader.result as string);
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                            />
                          </label>
                          <p className="text-xs text-gray-600 mt-1">PNG, JPG up to 2MB</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Company Name"
                        value={settings.company?.companyName || ''}
                        onChange={(v) => updateSetting('company', 'companyName', v)}
                        placeholder="Your Company Name"
                      />
                      <InputField
                        label="Legal Name"
                        value={settings.company?.legalName || ''}
                        onChange={(v) => updateSetting('company', 'legalName', v)}
                        placeholder="Legal Entity Name LLC"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="License Number"
                        value={settings.company?.licenseNumber || ''}
                        onChange={(v) => updateSetting('company', 'licenseNumber', v)}
                        placeholder="LIC-123456"
                      />
                      <InputField
                        label="GST Number"
                        value={settings.company?.gstNumber || ''}
                        onChange={(v) => updateSetting('company', 'gstNumber', v)}
                        placeholder="e.g. 123456789 RT0001"
                      />
                    </div>
                  </div>
                </SettingsCard>

                {/* Address */}
                <SettingsCard title="Business Address" description="Primary business location">
                  <div className="space-y-4">
                    <InputField
                      label="Street Address"
                      value={settings.company?.address || ''}
                      onChange={(v) => updateSetting('company', 'address', v)}
                      placeholder="123 Main Street"
                    />

                    <div className="grid grid-cols-3 gap-4">
                      <InputField
                        label="City"
                        value={settings.company?.city || ''}
                        onChange={(v) => updateSetting('company', 'city', v)}
                        placeholder="City"
                      />
                      <InputField
                        label="State/Province"
                        value={settings.company?.state || ''}
                        onChange={(v) => updateSetting('company', 'state', v.toUpperCase())}
                        placeholder="ST"
                      />
                      <InputField
                        label="ZIP/Postal Code"
                        value={settings.company?.zipCode || ''}
                        onChange={(v) => updateSetting('company', 'zipCode', v)}
                        placeholder="12345"
                      />
                    </div>

                    <SelectField
                      label="Country"
                      value={settings.company?.country || 'US'}
                      onChange={(v) => updateSetting('company', 'country', v)}
                      options={[
                        { value: 'US', label: 'United States' },
                        { value: 'CA', label: 'Canada' },
                      ]}
                    />
                  </div>
                </SettingsCard>

                {/* Contact Info */}
                <SettingsCard title="Contact Information" description="Company contact details">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Phone"
                        type="tel"
                        value={settings.company?.phone || ''}
                        onChange={(v) => updateSetting('company', 'phone', v)}
                        placeholder="(555) 123-4567"
                      />
                      <InputField
                        label="Fax"
                        type="tel"
                        value={settings.company?.fax || ''}
                        onChange={(v) => updateSetting('company', 'fax', v)}
                        placeholder="(555) 123-4568"
                      />
                    </div>

                    <InputField
                      label="Email"
                      type="email"
                      value={settings.company?.email || ''}
                      onChange={(v) => updateSetting('company', 'email', v)}
                      placeholder="info@yourcompany.com"
                    />

                    <InputField
                      label="Website"
                      type="url"
                      value={settings.company?.website || ''}
                      onChange={(v) => updateSetting('company', 'website', v)}
                      placeholder="https://www.yourcompany.com"
                    />
                  </div>
                </SettingsCard>

                {/* Branding */}
                <SettingsCard title="Branding Colors" description="Customize your brand colors">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">Primary Color</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={settings.company?.primaryColor || '#2C5282'}
                            onChange={(e) => updateSetting('company', 'primaryColor', e.target.value)}
                            className="h-10 w-16 rounded border border-gray-200"
                          />
                          <input
                            type="text"
                            value={settings.company?.primaryColor || '#2C5282'}
                            onChange={(e) => updateSetting('company', 'primaryColor', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">Secondary Color</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={settings.company?.secondaryColor || '#38A169'}
                            onChange={(e) => updateSetting('company', 'secondaryColor', e.target.value)}
                            className="h-10 w-16 rounded border border-gray-200"
                          />
                          <input
                            type="text"
                            value={settings.company?.secondaryColor || '#38A169'}
                            onChange={(e) => updateSetting('company', 'secondaryColor', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </SettingsCard>

                {/* Document Settings */}
                <SettingsCard title="Document Footers" description="Default text for quotes and emails">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">Quote Footer</label>
                      <textarea
                        value={settings.company?.quoteFooter || ''}
                        onChange={(e) => updateSetting('company', 'quoteFooter', e.target.value)}
                        placeholder="This quote is valid for 30 days."
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">Email Signature</label>
                      <textarea
                        value={settings.company?.emailSignature || ''}
                        onChange={(e) => updateSetting('company', 'emailSignature', e.target.value)}
                        placeholder="Best regards,&#10;Your Company Name"
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                </SettingsCard>

                {/* Insurance & Bonding */}
                <SettingsCard title="Insurance & Bonding" description="Insurance and bonding information for documents and contracts">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Insurance Provider"
                        value={settings.company?.insuranceProvider || ''}
                        onChange={(v) => updateSetting('company', 'insuranceProvider', v)}
                        placeholder="Insurance Company Name"
                      />
                      <InputField
                        label="Policy Number"
                        value={settings.company?.insurancePolicyNumber || ''}
                        onChange={(v) => updateSetting('company', 'insurancePolicyNumber', v)}
                        placeholder="POL-123456"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="CGL Limit"
                        value={settings.company?.insuranceCglLimit || ''}
                        onChange={(v) => updateSetting('company', 'insuranceCglLimit', v)}
                        placeholder="e.g. $2,000,000"
                      />
                      <InputField
                        label="Policy Expiration"
                        type="date"
                        value={settings.company?.insuranceExpiration || ''}
                        onChange={(v) => updateSetting('company', 'insuranceExpiration', v)}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Bonding Company"
                        value={settings.company?.bondingCompany || ''}
                        onChange={(v) => updateSetting('company', 'bondingCompany', v)}
                        placeholder="Bonding Company Name"
                      />
                      <InputField
                        label="Bond Amount"
                        type="number"
                        value={settings.company?.bondAmount || 0}
                        onChange={(v) => updateSetting('company', 'bondAmount', parseFloat(v) || 0)}
                        prefix="$"
                        min={0}
                      />
                    </div>
                  </div>
                </SettingsCard>

                {/* WCB / WorkSafeBC */}
                <SettingsCard title="WCB / WorkSafeBC" description="Workers' compensation registration and coverage details">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="WCB Account Number"
                        value={settings.company?.wcbAccount || ''}
                        onChange={(v) => updateSetting('company', 'wcbAccount', v)}
                        placeholder="e.g. 123456-01"
                      />
                      <InputField
                        label="Industry Classification"
                        value={settings.company?.wcbClassification || ''}
                        onChange={(v) => updateSetting('company', 'wcbClassification', v)}
                        placeholder="e.g. Carpentry — 721003"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        label="Assessment Rate (per $100 payroll)"
                        value={settings.company?.wcbAssessmentRate || ''}
                        onChange={(v) => updateSetting('company', 'wcbAssessmentRate', v)}
                        placeholder="e.g. 3.19"
                      />
                      <InputField
                        label="Coverage Effective Date"
                        type="date"
                        value={settings.company?.wcbEffectiveDate || ''}
                        onChange={(v) => updateSetting('company', 'wcbEffectiveDate', v)}
                      />
                    </div>

                    <InputField
                      label="Last Clearance Letter Date"
                      type="date"
                      value={settings.company?.wcbClearanceLetterDate || ''}
                      onChange={(v) => updateSetting('company', 'wcbClearanceLetterDate', v)}
                      helpText="Date of most recent good-standing clearance letter from WorkSafeBC"
                    />
                  </div>
                </SettingsCard>
              </div>
            )}

            {activeTab === 'agreements' && (() => {
              const ag = settings.agreements || {};

              const STANDARD_CLAUSES: { key: string; label: string; description: string }[] = [
                { key: 'clause_change_orders',      label: 'Change Orders',                   description: 'Governs how scope changes must be authorized before work proceeds.' },
                { key: 'clause_warranty',           label: 'Warranty & Deficiencies',         description: 'Workmanship warranty period and deficiency remedy obligations.' },
                { key: 'clause_lien_rights',        label: 'BC Builders Lien Act Compliance', description: 'Holdback, lien rights, and statutory declaration obligations.' },
                { key: 'clause_confidentiality',    label: 'Confidentiality & Client Privacy', description: 'Non-disclosure of client data, no-photo policy, 2-year survival.' },
                { key: 'clause_termination',        label: 'Termination',                     description: 'For convenience (5 days\' notice) and for cause provisions.' },
                { key: 'clause_dispute',            label: 'Dispute Resolution',              description: 'Negotiation → mediation → arbitration under BC law.' },
                { key: 'clause_general_conditions', label: 'General Conditions',              description: 'Governing law, entire agreement, amendments, severability, notices, e-signatures.' },
              ];

              const WO_CLAUSES: { key: string; label: string; description: string }[] = [
                { key: 'clause_schedule_time',           label: 'Schedule & Time',                    description: 'Time is of the essence, delay notification, force-majeure extensions.' },
                { key: 'clause_independent_contractor',  label: 'Independent Contractor Status',      description: 'Subcontractor is not an employee; responsible for own deductions and payroll.' },
                { key: 'clause_wcb_ohs',                 label: 'WCB & Occupational Health & Safety', description: 'WCB account, clearance letter, OHS Regulation compliance, WHMIS.' },
                { key: 'clause_insurance_requirements',  label: 'Insurance Requirements',             description: 'CGL $2M minimum, auto liability, tools & equipment, OchrePoint as additional insured.' },
              ];

              return (
                <div className="space-y-6">
                  <SettingsCard
                    title="Standard Clause Templates"
                    description="Default text for clauses included in Work Agreements (client contracts). Edit these to match your standard terms — they pre-fill every new agreement."
                  >
                    <div className="space-y-8">
                      {STANDARD_CLAUSES.map(({ key, label, description }) => (
                        <div key={key}>
                          <div className="flex items-start justify-between mb-1.5">
                            <div>
                              <p className="text-sm font-semibold text-gray-800">{label}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                            </div>
                            <span className="text-[10px] font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded flex-shrink-0 ml-4">{key.replace('clause_', '')}</span>
                          </div>
                          <textarea
                            rows={6}
                            value={(ag as Record<string,string>)[key] ?? CLAUSE_TEXT[key.replace('clause_', '') as keyof typeof CLAUSE_TEXT] ?? ''}
                            onChange={e => updateSetting('agreements', key, e.target.value)}
                            placeholder="Clause text…"
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-y font-mono leading-relaxed"
                          />
                        </div>
                      ))}
                    </div>
                  </SettingsCard>

                  <SettingsCard
                    title="Subcontractor Work Order Clause Templates"
                    description="Default text for clauses specific to Subcontractor Work Orders (when OchrePoint is the sub). These appear in addition to the standard clauses above."
                  >
                    <div className="space-y-8">
                      {WO_CLAUSES.map(({ key, label, description }) => (
                        <div key={key}>
                          <div className="flex items-start justify-between mb-1.5">
                            <div>
                              <p className="text-sm font-semibold text-gray-800">{label}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                            </div>
                            <span className="text-[10px] font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded flex-shrink-0 ml-4">{key.replace('clause_', '')}</span>
                          </div>
                          <textarea
                            rows={6}
                            value={(ag as Record<string,string>)[key] ?? CLAUSE_TEXT[key.replace('clause_', '') as keyof typeof CLAUSE_TEXT] ?? ''}
                            onChange={e => updateSetting('agreements', key, e.target.value)}
                            placeholder="Clause text…"
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-y font-mono leading-relaxed"
                          />
                        </div>
                      ))}
                    </div>
                  </SettingsCard>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
                    <span className="material-symbols-outlined text-amber-600 flex-shrink-0 mt-0.5">info</span>
                    <div className="text-sm text-amber-800">
                      <p className="font-semibold mb-1">How templates work</p>
                      <p>These templates pre-fill the clause text when you create a <strong>new</strong> agreement. Existing agreements are not affected — their clauses are stored independently and can still be edited per-agreement in the builder.</p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {activeTab === 'integrations' && (
              <IntegrationsTab />
            )}

            {activeTab === 'invites' && (
              <InvitesTab />
            )}

            {/* Save/Cancel Buttons - Fixed at bottom */}
            {activeTab !== 'integrations' && activeTab !== 'invites' && <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 -mx-6 px-6 py-4 mt-8 flex justify-end gap-3">
              <button
                onClick={handleCancel}
                disabled={!hasChanges || saving}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="px-6 py-2 text-sm font-semibold text-white bg-primary border border-transparent rounded-lg shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-lg">save</span>
                    Save Changes
                  </>
                )}
              </button>
            </div>}
          </>
        )}
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </MainLayout>
  );
};
